/**
 * useArtifactSync -- debounce + push live editor / canvas state to the
 * interview-service WebSocket.
 *
 * Contract:
 *   - Caller calls `update(payload)` from the editor's `onChange` (no
 *     throttling on their side; we own it).
 *   - We send the latest payload over the WS after `debounceMs` of
 *     quiet (default 2500ms) -- shorter than the server-side relay
 *     interval (8s) so we never miss the most recent state on a pause.
 *   - We additionally send a forced flush on `flushNow()` (used for
 *     discrete events like "run tests" or "submit").
 *
 * The server in turn applies its OWN 8s minimum relay interval to the
 * Azure Realtime conversation -- so even if we send every 2.5s, the AI
 * only sees a snapshot every 8s, which keeps the conversation natural.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type Kind = 'code' | 'canvas' | 'numbers' | 'notepad';

interface UseArtifactSyncArgs {
  kind: Kind;
  enabled: boolean;
  /** WS sender from useInterviewSocket. */
  sendArtifact: (kind: Kind, payload: Record<string, unknown>) => boolean | void;
  /** Optional millisecond debounce; default 2500. */
  debounceMs?: number;
}

export function useArtifactSync({
  kind,
  enabled,
  sendArtifact,
  debounceMs = 2500,
}: UseArtifactSyncArgs) {
  const latestRef = useRef<Record<string, unknown> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Exposed to the UI so panes can flash an "AI sees your edit" indicator.
  const [lastPushedAt, setLastPushedAt] = useState<number | null>(null);
  const [pendingEdit, setPendingEdit] = useState(false);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!enabled) return;
    const payload = latestRef.current;
    if (!payload) return;
    try {
      const ok = sendArtifact(kind, payload);
      if (ok !== false) {
        setLastPushedAt(Date.now());
        setPendingEdit(false);
      }
    } catch (e) {
      console.warn('useArtifactSync: send failed', e);
    }
  }, [enabled, kind, sendArtifact]);

  const update = useCallback(
    (payload: Record<string, unknown>) => {
      latestRef.current = payload;
      if (!enabled) return;
      setPendingEdit(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, debounceMs);
    },
    [enabled, debounceMs, flush],
  );

  // Best-effort flush on unmount -- the candidate may have closed the tab
  // mid-edit and we still want the recruiter to see whatever was on screen.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      // Don't auto-send here: the WS may already be closed. But the
      // server-side last-saved state is enough; closure is acceptable.
    };
  }, []);

  // Page-lifecycle safety net: if the candidate closes the tab or the
  // browser navigates away mid-edit, fire the latest snapshot in the
  // last possible moment before the WS gets torn down. `pagehide` is
  // the most reliable event across mobile + desktop; `beforeunload`
  // is a fallback for older browsers. Both run synchronously, so the
  // sendArtifact() call has a real chance to land before close.
  useEffect(() => {
    if (!enabled) return undefined;
    const fire = () => {
      const payload = latestRef.current;
      if (!payload) return;
      try { sendArtifact(kind, payload); } catch { /* swallow */ }
    };
    window.addEventListener('pagehide', fire);
    window.addEventListener('beforeunload', fire);
    return () => {
      window.removeEventListener('pagehide', fire);
      window.removeEventListener('beforeunload', fire);
    };
  }, [enabled, kind, sendArtifact]);

  return { update, flush, lastPushedAt, pendingEdit };
}
